/**
 * -----------------------------------------------------------------------------
 * Project  : ReelConsensus app - Frontend Page: Register
 * File     : Register.js
 * Author   : Zachary Dry
 * Created  : 2025-09-18
 * Updated  : 2025-09-19
 * Version  : 1.0
 * License: Proprietary (Company Confidential)
 * -----------------------------------------------------------------------------
 * Description:
 *   React page component rendering the Register view and coordinating data fetching.
 *   Exports: Register
 *   
 *
 * Dependencies:
 *   - ../context/AuthContext
 *   - @mui/material
 *   - react
 *   - react-router-dom
 *
 * Usage:
 *   Use in Router: <Route path="/register" element={<Register />} />
 * -----------------------------------------------------------------------------
 */
import React, { useEffect, useState } from 'react';
import { TextField, Button, Paper, Typography, Box, Stack } from '@mui/material';
import { buildApiUrl, verifyEmail, resendCode } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { saveToken } from '../services/auth';
import { useNavigate } from 'react-router-dom';

const Register = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: '', email: '', password: '' });
  const [error, setError] = useState('');
  const [stage, setStage] = useState('form'); // 'form' | 'verify'
  const [countdown, setCountdown] = useState(60);
  const [code, setCode] = useState('');
  const [resends, setResends] = useState(0);

  useEffect(() => {
    if (stage !== 'verify') return;
    setCountdown(60);
    const t = setInterval(() => setCountdown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [stage]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!window.isSecureContext) {
      setError('Registration is only allowed over a secure connection (HTTPS). Please use https:// to protect your password.');
      return;
    }
    try {
      const email = (form.email || '').toLowerCase().trim();
      const params = new URLSearchParams(window.location.search);
      const referral = JSON.parse(localStorage.getItem('ref:first') || 'null') || {
        referrer: document.referrer || '',
        utmSource: params.get('utm_source') || '',
        utmMedium: params.get('utm_medium') || '',
        utmCampaign: params.get('utm_campaign') || '',
        utmContent: params.get('utm_content') || '',
        utmTerm: params.get('utm_term') || ''
      };
      const resp = await register({ ...form, username: (form.username || '').toLowerCase().trim(), email,
        firstReferrer: referral.referrer,
        firstUtmSource: referral.utmSource,
        firstUtmMedium: referral.utmMedium,
        firstUtmCampaign: referral.utmCampaign,
        firstUtmContent: referral.utmContent,
        firstUtmTerm: referral.utmTerm
      });
      // If server returns 202 (handled in service), we won't have token yet; move to verify stage
      if (!resp?.token) {
        setStage('verify');
        return;
      }
      navigate('/profile?new=1');
    } catch (e) {
      const backendMsg = e?.response?.data?.error || '';
      const isConflict = e?.response?.status === 409;
      const msg = isConflict
        ? (backendMsg || 'Username or email already exists.')
        : (backendMsg || 'Registration failed');
      setError(msg);
    }
  };

  const onVerify = async () => {
    setError('');
    try {
      const email = (form.email || '').toLowerCase().trim();
      const d = await verifyEmail(email, code.trim());
      if (d?.token) {
        try { saveToken(d.token); } catch (_) {}
      }
      navigate('/profile?new=1');
    } catch (e) {
      const status = e?.response?.status;
      setError(e?.response?.data?.error || 'Verification failed');
      if (status === 400 || status === 429) {
        // keep user on verify
      }
    }
  };

  const onResend = async () => {
    if (countdown > 0) return;
    if (resends >= 5) { setError('Too many resend attempts. Try later.'); return; }
    setError('');
    try {
      const email = (form.email || '').toLowerCase().trim();
      await resendCode(email);
      setResends((n) => n + 1);
      setCountdown(60);
    } catch (e) {
      setError(e?.response?.data?.error || 'Resend failed');
    }
  };

  return (
    <Paper sx={{ p: 3, maxWidth: 400 }}>
      <Typography variant="h5" gutterBottom>Register</Typography>
      {stage === 'form' ? (
        <Box component="form" onSubmit={onSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField label="Username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} required helperText="We lowercase usernames automatically." />
          <TextField label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required helperText="We store emails in lowercase." />
          <TextField label="Password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
          <Typography variant="caption" color="text.secondary">
            We will send a 6-digit verification code to your email. It expires in 5 minutes. Check your spam folder if you don't see it.
          </Typography>
          {error && <Typography color="error">{error}</Typography>}
          <Button type="submit" variant="contained">Sign up</Button>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2">Enter the 6-digit code we sent to <strong>{(form.email || '').toLowerCase().trim()}</strong>.</Typography>
          <TextField label="Verification code" value={code} onChange={(e) => setCode(e.target.value)} inputProps={{ maxLength: 6 }} />
          <Stack direction="row" spacing={1} alignItems="center">
            <Button variant="contained" onClick={onVerify} disabled={!code || code.trim().length !== 6}>Verify</Button>
            <Button variant="text" onClick={onResend} disabled={countdown > 0}>Resend code</Button>
            <Typography variant="caption" color="text.secondary">{countdown > 0 ? `You can resend in ${countdown}s` : (resends >= 5 ? 'Retry limit reached' : '')}</Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">Code expires 5 minutes after the last send.</Typography>
          {error && <Typography color="error">{error}</Typography>}
        </Box>
      )}
      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <Button onClick={() => {
          const cid = process.env.REACT_APP_DISCORD_CLIENT_ID;
          const url = buildApiUrl('/api/auth/discord/login') + (cid ? `?client_id=${encodeURIComponent(cid)}` : '');
          window.location.href = url;
        }} variant="outlined">Register with Discord</Button>
      </Stack>
    </Paper>
  );
};

export default Register;


